import React, { FC, useEffect, useRef } from 'react';
import { useValidationControl, ValidationControlProps } from 'src/shared/validation';
import styled from 'styled-components';
import { Link } from '../buttons';
import { HelpIcon, HelpIconContainer } from '../helpIconContainer';
import { CheckboxIndeterminate } from '../svg/checkbox-indeterminate';
import { CheckboxSelected } from '../svg/checkbox-selected';
import ValidationMessage from '../validation/validationMessage';
import { ZTooltip } from '../ztooltip/ztooltip';

/** `ai` sets the colour, `compact` sets the density; `compact-ai` is both. */
export type CheckboxVariant = 'default' | 'ai' | 'compact' | 'compact-ai';

const isAiVariant = (variant: CheckboxVariant) => variant === 'ai' || variant === 'compact-ai';
const isCompactVariant = (variant: CheckboxVariant) => variant === 'compact' || variant === 'compact-ai';

const CHECKBOX_SIZE = '16px';
const CHECKBOX_LABEL_GAP = '5px';
const CHECKBOX_TEXT_OFFSET = `calc(${CHECKBOX_SIZE} + ${CHECKBOX_LABEL_GAP})`;

const CaptionContainer = styled.div`
    display: flex;
    margin-left: ${CHECKBOX_TEXT_OFFSET};
    margin-top: 4px;
`;

export const CheckboxContainer = styled.div<{ disabled?: boolean; $hasCaption?: boolean; $isError?: boolean }>`
    margin-right: 10px;
    cursor: default;
    opacity: ${(props) => (props.disabled ? '0.6' : '1')};
    display: inline-flex;
    border-radius: 4px;
    ${(props) => props.$isError && 'box-shadow: 0 0 0 2px rgba(222, 28, 34, 0.2);'}

    ${(props) =>
        props.$hasCaption &&
        `
        align-items: flex-start;
        flex-direction: column;
    `}
`;

const CheckboxInput = styled.input<{
    disabled?: boolean;
    checked?: boolean;
    $indeterminate?: boolean;
    $variant: CheckboxVariant;
}>`
    cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
    width: ${CHECKBOX_SIZE};
    height: ${CHECKBOX_SIZE};
    box-sizing: border-box;
    flex-shrink: 0;
    appearance: none;
    border-radius: 3px;
    transition:
        background 0.2s ease,
        border-color 0.2s ease;

    background: ${(props) => {
        if (props.disabled) return props.theme.app.component.checkbox.selected.disabled.inputColor;
        if (props.checked) {
            return isAiVariant(props.$variant) ? props.theme.ai.gradient : props.theme.app.component.checkbox.selected.active.background;
        }
        return props.theme.app.component.checkbox.unselected.active.background;
    }};

    border: 1px solid
        ${(props) => {
            if (props.disabled) return props.theme.app.component.checkbox.selected.disabled.border;
            if (props.checked) {
                return isAiVariant(props.$variant) ? props.theme.ai.solid : props.theme.app.component.checkbox.selected.active.border;
            }
            return '#d1d5db';
        }};

    opacity: ${(props) => (props.disabled ? '0.6' : '1')};
`;

const CheckboxWrapper = styled.div`
    position: relative;
    width: ${CHECKBOX_SIZE};
    height: ${CHECKBOX_SIZE};
    flex: 0 0 ${CHECKBOX_SIZE};
    display: inline-flex;
    margin-top: 2px;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    outline: none;

    > svg {
        position: absolute;
        width: ${CHECKBOX_SIZE};
        height: ${CHECKBOX_SIZE};
        left: 1px;
        pointer-events: none;
    }
`;

const CheckboxLabelContainer = styled.div<{ $variant: CheckboxVariant }>`
    padding-top: 0;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0;
    min-height: ${(props) => (isCompactVariant(props.$variant) ? '16px' : '20px')};

    > label {
        margin-left: ${CHECKBOX_LABEL_GAP};
        min-width: 0;
        line-height: ${(props) => (isCompactVariant(props.$variant) ? '16px' : '20px')};
        font-size: ${(props) => (isCompactVariant(props.$variant) ? '12px' : '14px')};
        color: ${(props) => (isCompactVariant(props.$variant) ? '#374151' : '#263238')};
        white-space: pre-wrap;
        text-overflow: ellipsis;
        overflow: hidden;
        cursor: pointer;
    }
`;

const CheckboxCaption = styled.span`
    display: inline-block;
    font-size: 12px;
    line-height: 1.4;
    color: #78909c;
`;

const StyledHelpIconContainer = styled(HelpIconContainer)`
    margin-left: 5px;
    flex: 0 0 16px;
    box-shadow: none;
`;

const ParentContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: flex-start;
`;

const BadgeContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;

    > *:first-child {
        margin-left: 12px;
    }
`;

interface ReadMore {
    label: string;
    link: string;
}

export interface CheckboxProps extends ValidationControlProps<boolean> {
    checked: boolean;
    indeterminate?: boolean;
    label?: React.ReactNode;
    caption?: React.ReactNode;
    className?: string;
    disabled?: boolean;
    disabledTooltip?: string | React.ReactNode;
    helpTooltip?: React.ReactNode | string | null;
    helpTooltipPosition?: 'left' | 'top' | 'right' | 'bottom';
    iconTooltip?: React.ReactNode;
    allowChangeOnDisabled?: boolean;
    readMore?: ReadMore;
    onChange: (isChecked: boolean) => void;
    onClick?: (e: React.MouseEvent) => void;
    variant?: CheckboxVariant;
    badges?: React.ReactNode[];
}

export const _Checkbox: FC<CheckboxProps> = (props) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const validation = useValidationControl<HTMLDivElement, boolean>(props, props.checked);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.indeterminate = Boolean(props.indeterminate);
        }
    }, [props.indeterminate]);

    const handleChange = (isChecked: boolean) => {
        if (!props.disabled || props.allowChangeOnDisabled) props.onChange(isChecked);
    };

    const variant = props.variant ?? 'default';

    const showDisabledTooltip = props.disabled && props.disabledTooltip;

    const content = (
        <CheckboxContainer
            ref={validation.setRef}
            disabled={props.disabled}
            $hasCaption={!!props.caption}
            $isError={validation.isValidationError}
            onClick={(e) => {
                e.stopPropagation();
                props.onClick?.(e);
            }}
        >
            <ParentContainer>
                <CheckboxWrapper>
                    <CheckboxInput
                        ref={inputRef}
                        type='checkbox'
                        checked={props.checked}
                        disabled={props.disabled}
                        $indeterminate={props.indeterminate}
                        onChange={(e) => handleChange(e.target.checked)}
                        $variant={variant}
                    />
                    {props.checked && (props.indeterminate ? <CheckboxIndeterminate /> : <CheckboxSelected />)}
                </CheckboxWrapper>

                <CheckboxLabelContainer $variant={variant}>
                    {props.label && <label onClick={() => handleChange(!props.checked)}>{props.label}</label>}

                    {props.readMore && (
                        <Link target={'_blank'} css={'margin-left:3px'} href={props.readMore.link}>
                            {props.readMore.label}
                        </Link>
                    )}

                    {props.helpTooltip && (
                        <StyledHelpIconContainer tooltip={props.helpTooltip} position={props.helpTooltipPosition}>
                            {props.iconTooltip || <HelpIcon />}
                        </StyledHelpIconContainer>
                    )}

                    {props.badges && props.badges.length > 0 && (
                        <BadgeContainer>
                            {props.badges.map((badge, index) => (
                                <React.Fragment key={index}>{badge}</React.Fragment>
                            ))}
                        </BadgeContainer>
                    )}
                </CheckboxLabelContainer>
            </ParentContainer>

            {props.caption && (
                <CaptionContainer>
                    <CheckboxCaption>{props.caption}</CheckboxCaption>
                </CaptionContainer>
            )}

            {validation.validationKey && <ValidationMessage validationKey={validation.validationKey} deferUntilSubmit />}
        </CheckboxContainer>
    );

    if (showDisabledTooltip) {
        return (
            <ZTooltip addContainer html={props.disabledTooltip}>
                {content}
            </ZTooltip>
        );
    }

    return content;
};

export const Checkbox = styled(_Checkbox)``;
